import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  appStoreReleasesApi,
  type AppStoreAppCatalogResponse,
  type AppStoreAsoImportRequest,
  type AppStoreDiffResponse,
  type AppStoreDraftScreenshotAttachRequest,
  type AppStoreDraftCreateRequest,
  type AppStoreDraftPatchRequest,
  type AppStoreDraftResponse,
  type AppStoreDraftScreenshotUploadRequest,
  type AppStoreDraftValidateRequest,
  type AppStoreReleaseApprovalInfoRequest,
  type AppStoreReleaseApprovalInfoResponse,
  type AppStoreReleaseApprovalRequest,
  type AppStoreReleaseApprovalResponse,
  type AppStoreReleaseSubmissionResponse,
  type AppStoreReleaseSubmitRequest,
  type AppStoreReleaseVerifyPublicStoreResponse,
  type AppStoreSnapshotResponse,
  type AppStoreSpecContractResponse,
  type AppStoreUserImageFileListResponse,
  type AppStoreValidationResponse,
} from '@/lib/api/appStoreReleases';

export const appStoreReleaseKeys = {
  all: ['app-store-releases'] as const,
  apps: () => [...appStoreReleaseKeys.all, 'apps'] as const,
  snapshot: (appKey: string) => [...appStoreReleaseKeys.all, 'snapshot', appKey] as const,
  spec: (specVersion: string) => [...appStoreReleaseKeys.all, 'spec', specVersion] as const,
  userImageFiles: () => [...appStoreReleaseKeys.all, 'user-image-files'] as const,
  draft: (draftId: string) => [...appStoreReleaseKeys.all, 'draft', draftId] as const,
  diff: (draftId: string) => [...appStoreReleaseKeys.draft(draftId), 'diff'] as const,
  approvalInfo: (
    draftId: string,
    draftRevisionId: string,
    buildId: string,
    releaseMode: string,
    reviewNotes: string,
    validationRunId: string,
  ) =>
    [
      ...appStoreReleaseKeys.draft(draftId),
      'approval-info',
      draftRevisionId,
      buildId,
      releaseMode,
      reviewNotes,
      validationRunId,
    ] as const,
  submission: (submissionId: string) => [...appStoreReleaseKeys.all, 'submission', submissionId] as const,
};

export function useAppStoreApps() {
  return useQuery<AppStoreAppCatalogResponse, Error>({
    queryKey: appStoreReleaseKeys.apps(),
    queryFn: () => appStoreReleasesApi.listApps(),
  });
}

export function useDiscoverAppStoreApps() {
  const queryClient = useQueryClient();

  return useMutation<AppStoreAppCatalogResponse, Error>({
    mutationFn: () => appStoreReleasesApi.discoverApps(),
    onSuccess: (catalog) => {
      queryClient.setQueryData(appStoreReleaseKeys.apps(), catalog);
    },
  });
}

export function useAppStoreSnapshot(appKey: string) {
  return useQuery<AppStoreSnapshotResponse, Error>({
    queryKey: appStoreReleaseKeys.snapshot(appKey),
    queryFn: () => appStoreReleasesApi.getSnapshot(appKey),
    enabled: appKey.length > 0,
  });
}

export function useAppStoreSpec(specVersion: string | null) {
  return useQuery<AppStoreSpecContractResponse, Error>({
    queryKey: appStoreReleaseKeys.spec(specVersion ?? ''),
    queryFn: () => appStoreReleasesApi.getSpec(specVersion as string),
    enabled: Boolean(specVersion),
  });
}

export function useAppStoreUserImageFiles(enabled = true) {
  return useQuery<AppStoreUserImageFileListResponse, Error>({
    queryKey: appStoreReleaseKeys.userImageFiles(),
    queryFn: () => appStoreReleasesApi.listUserImageFiles(),
    enabled,
  });
}

export function useSyncAppStore(appKey: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => appStoreReleasesApi.syncApp(appKey),
    onSuccess: (snapshot) => {
      queryClient.setQueryData(appStoreReleaseKeys.snapshot(appKey), snapshot);
      queryClient.invalidateQueries({ queryKey: appStoreReleaseKeys.spec(snapshot.spec_version) });
    },
  });
}

export function useCreateAppStoreDraft(appKey: string) {
  const queryClient = useQueryClient();

  return useMutation<AppStoreDraftResponse, Error, AppStoreDraftCreateRequest>({
    mutationFn: (request) => appStoreReleasesApi.createDraft(appKey, request),
    onSuccess: (draft) => {
      queryClient.setQueryData(appStoreReleaseKeys.draft(draft.id), draft);
      queryClient.invalidateQueries({ queryKey: appStoreReleaseKeys.diff(draft.id) });
    },
  });
}

export function usePatchAppStoreDraft(draftId: string | null) {
  const queryClient = useQueryClient();

  return useMutation<AppStoreDraftResponse, Error, AppStoreDraftPatchRequest>({
    mutationFn: (request) => {
      if (!draftId) {
        throw new Error('No App Store draft is selected');
      }
      return appStoreReleasesApi.patchDraft(draftId, request);
    },
    onSuccess: (draft) => {
      queryClient.setQueryData(appStoreReleaseKeys.draft(draft.id), draft);
      queryClient.invalidateQueries({ queryKey: appStoreReleaseKeys.diff(draft.id) });
    },
  });
}

export function useImportAsoIntoDraft(draftId: string | null) {
  const queryClient = useQueryClient();

  return useMutation<AppStoreDraftResponse, Error, AppStoreAsoImportRequest>({
    mutationFn: (request) => {
      if (!draftId) {
        throw new Error('No App Store draft is selected');
      }
      return appStoreReleasesApi.importAso(draftId, request);
    },
    onSuccess: (draft) => {
      queryClient.setQueryData(appStoreReleaseKeys.draft(draft.id), draft);
      queryClient.invalidateQueries({ queryKey: appStoreReleaseKeys.diff(draft.id) });
    },
  });
}

export function useUploadAppStoreScreenshot(draftId: string | null) {
  const queryClient = useQueryClient();

  return useMutation<AppStoreDraftResponse, Error, AppStoreDraftScreenshotUploadRequest>({
    mutationFn: (request) => {
      if (!draftId) {
        throw new Error('No App Store draft is selected');
      }
      return appStoreReleasesApi.uploadDraftScreenshot(draftId, request);
    },
    onSuccess: (draft) => {
      queryClient.setQueryData(appStoreReleaseKeys.draft(draft.id), draft);
      queryClient.invalidateQueries({ queryKey: appStoreReleaseKeys.diff(draft.id) });
      queryClient.invalidateQueries({ queryKey: appStoreReleaseKeys.userImageFiles() });
    },
  });
}

export function useAttachAppStoreScreenshot(draftId: string | null) {
  const queryClient = useQueryClient();

  return useMutation<AppStoreDraftResponse, Error, AppStoreDraftScreenshotAttachRequest>({
    mutationFn: (request) => {
      if (!draftId) {
        throw new Error('No App Store draft is selected');
      }
      return appStoreReleasesApi.attachDraftScreenshot(draftId, request);
    },
    onSuccess: (draft) => {
      queryClient.setQueryData(appStoreReleaseKeys.draft(draft.id), draft);
      queryClient.invalidateQueries({ queryKey: appStoreReleaseKeys.diff(draft.id) });
    },
  });
}

export function useAppStoreDraftDiff(draftId: string | null) {
  return useQuery<AppStoreDiffResponse, Error>({
    queryKey: appStoreReleaseKeys.diff(draftId ?? ''),
    queryFn: () => appStoreReleasesApi.getDiff(draftId as string),
    enabled: Boolean(draftId),
  });
}

export function useValidateAppStoreDraft(draftId: string | null) {
  return useMutation<AppStoreValidationResponse, Error, AppStoreDraftValidateRequest>({
    mutationFn: (request) => {
      if (!draftId) {
        throw new Error('No App Store draft is selected');
      }
      return appStoreReleasesApi.validateDraft(draftId, request);
    },
  });
}

export function useAppStoreApprovalInfo(
  draftId: string | null,
  request: AppStoreReleaseApprovalInfoRequest | null,
) {
  return useQuery<AppStoreReleaseApprovalInfoResponse, Error>({
    queryKey: appStoreReleaseKeys.approvalInfo(
      draftId ?? '',
      request?.draft_revision_id ?? '',
      request?.build_id ?? '',
      request?.release_mode ?? '',
      request?.review_notes ?? '',
      request?.validation_run_id ?? '',
    ),
    queryFn: () => appStoreReleasesApi.getApprovalInfo(draftId as string, request as AppStoreReleaseApprovalInfoRequest),
    enabled: Boolean(draftId && request),
  });
}

export function useApproveAppStoreDraft(draftId: string | null) {
  return useMutation<AppStoreReleaseApprovalResponse, Error, AppStoreReleaseApprovalRequest>({
    mutationFn: (request) => {
      if (!draftId) {
        throw new Error('No App Store draft is selected');
      }
      return appStoreReleasesApi.approveDraft(draftId, request);
    },
  });
}

export function useSubmitAppStoreRelease(snapshotId: string | null) {
  const queryClient = useQueryClient();

  return useMutation<AppStoreReleaseSubmissionResponse, Error, AppStoreReleaseSubmitRequest>({
    mutationFn: (request) => {
      if (!snapshotId) {
        throw new Error('No approved App Store snapshot is selected');
      }
      return appStoreReleasesApi.submitApprovedSnapshot(snapshotId, request);
    },
    onSuccess: (submission) => {
      queryClient.setQueryData(appStoreReleaseKeys.submission(submission.id), submission);
    },
  });
}

export function useAppStoreSubmission(submissionId: string | null) {
  return useQuery<AppStoreReleaseSubmissionResponse, Error>({
    queryKey: appStoreReleaseKeys.submission(submissionId ?? ''),
    queryFn: () => appStoreReleasesApi.getSubmission(submissionId as string),
    enabled: Boolean(submissionId),
  });
}

export function usePollAppStoreSubmission(submissionId: string | null) {
  const queryClient = useQueryClient();

  return useMutation<AppStoreReleaseSubmissionResponse, Error>({
    mutationFn: () => {
      if (!submissionId) {
        throw new Error('No App Store release submission is selected');
      }
      return appStoreReleasesApi.pollSubmission(submissionId);
    },
    onSuccess: (submission) => {
      queryClient.setQueryData(appStoreReleaseKeys.submission(submission.id), submission);
    },
  });
}

export function useVerifyAppStorePublicStore(submissionId: string | null) {
  return useMutation<AppStoreReleaseVerifyPublicStoreResponse, Error>({
    mutationFn: () => {
      if (!submissionId) {
        throw new Error('No App Store release submission is selected');
      }
      return appStoreReleasesApi.verifyPublicStore(submissionId);
    },
  });
}
